import _ from 'lodash';

import * as Service from 'types/service';
import { ClientBusinessDefinedState, dashboard } from 'types';
import { ServiceDefinedState } from 'state/service/config';
import { colorToClassName } from 'constants/partyColorConstants';
import { filterNilValues } from '@tock/shared/utils/typeUtils';
import { safeEnumCompare } from '@tock/coretypes';
import protos from 'utils/protos';

const { PartyState } = protos;

export function convertClientObjectToServerObject(
  businessDefinedState: ClientBusinessDefinedState
): dashboard.BusinessDefinedState {
  return {
    id: businessDefinedState.id || 0,
    color: businessDefinedState.color,
    ordering: businessDefinedState.ordering,
    primaryState: businessDefinedState.primaryState,
    secondaryState: businessDefinedState.secondaryState || '',
    canDelete: businessDefinedState.canDelete,
    description: businessDefinedState.description,
  };
}

export function updateBusinessDefinedStatesLists(
  stateList: ClientBusinessDefinedState[],
  newState: ClientBusinessDefinedState
): ClientBusinessDefinedState[] {
  const replacingIndex = _.findIndex(
    stateList,
    (state: ClientBusinessDefinedState) => state.id === newState.id,
    0
  );

  if (replacingIndex >= 0 && stateList[replacingIndex].ordering !== newState.ordering) {
    // Reordering, need to update ordering of other objects
    // Currently we only swap, items when we switch to drag and drop, update this TODO - LCN 03/04/19
    let listToSearchIn;
    const listOptions = filterIntoExpectedAndSeated(stateList);
    if (safeEnumCompare(newState.primaryState, PartyState, PartyState.SEATED)) {
      listToSearchIn = listOptions.seated;
    } else {
      listToSearchIn = listOptions.expected;
    }
    // Need to find the item that has the old ordering to swap
    const stateInOldPosition = _.find(
      listToSearchIn,
      (state: ClientBusinessDefinedState) => state.ordering === newState.ordering
    );
    if (!stateInOldPosition) {
      // if you can't find the other state, change nothing
      return stateList;
    }
    const indexOfOldState = _.findIndex(
      stateList,
      (state: ClientBusinessDefinedState) => state.key === stateInOldPosition.key
    );
    stateList[indexOfOldState].ordering = stateList[replacingIndex].ordering;
  }
  stateList[replacingIndex] = newState;
  return stateList;
}

interface HasPrimaryState {
  primaryState: Service.PartyState | string | number;
}

export function filterPartyStates<T extends HasPrimaryState>(
  states: T[]
): {
  [primaryState: string]: T[];
} {
  let expected: T[] = [];
  let seated: T[] = [];
  let noshow: T[] = [];
  let left: T[] = [];
  let canceled: T[] = [];
  _.forEach(states, (state) => {
    const primaryState = state.primaryState;
    if (safeEnumCompare(primaryState, PartyState, PartyState.SEATED)) {
      seated = _.concat(seated, state);
    } else if (
      safeEnumCompare(primaryState, PartyState, PartyState.ARRIVED) ||
      safeEnumCompare(primaryState, PartyState, PartyState.PARTIALLY_ARRIVED) ||
      safeEnumCompare(primaryState, PartyState, PartyState.PARTIALLY_SEATED) ||
      safeEnumCompare(primaryState, PartyState, PartyState.EXPECTED)
    ) {
      expected = _.concat(expected, state);
    } else if (safeEnumCompare(primaryState, PartyState, PartyState.LEFT)) {
      left = _.concat(left, state);
    } else if (safeEnumCompare(primaryState, PartyState, PartyState.NO_SHOW)) {
      noshow = _.concat(noshow, state);
    } else if (safeEnumCompare(primaryState, PartyState, PartyState.CANCELED)) {
      canceled = _.concat(canceled, state);
    }
  });
  return {
    expected,
    left,
    noshow,
    seated,
    canceled,
  };
}

export function filterIntoExpectedAndSeated<T extends HasPrimaryState>(states: T[]) {
  const result = filterPartyStates(states);
  return {
    expected: result.expected,
    seated: result.seated,
  };
}

export function isServiceDefinedPartyState(item: string): item is Service.PartyState {
  return (
    item === 'EXPECTED' ||
    item === 'SEATED' ||
    item === 'LEFT' ||
    item === 'NO_SHOW' ||
    item === 'CANCELED' ||
    item === 'ARRIVED' ||
    item === 'PARTIALLY_ARRIVED' ||
    item === 'PARTIALLY_SEATED'
  );
}

export const createSecondaryStates = function createSecondaryStates(
  businessDefinedStates: ServiceDefinedState[]
): Service.StateDescription[] {
  const stateDescriptions: Service.StateDescription[] = _.reduce(
    businessDefinedStates,
    (
      acc: Service.StateDescription[],
      businessDefinedState: ServiceDefinedState
    ): Service.StateDescription[] => {
      const classNamesForColor = colorToClassName[businessDefinedState.color] || {};
      if (!isServiceDefinedPartyState(businessDefinedState.primaryState)) {
        return acc;
      }

      acc.push({
        aerialTableBackgroundClassName: classNamesForColor.aerialTableBackground || '',
        aerialTableForegroundClassName: classNamesForColor.aerialTableForeground || '',
        borderClassName: classNamesForColor.borderColor || '',
        className: classNamesForColor.backgroundColor || '',
        ordering: businessDefinedState.ordering,
        primaryState: businessDefinedState.primaryState,
        secondaryValue: businessDefinedState.stateName,
        stateName: businessDefinedState.stateName,
        strokeColorClassName: classNamesForColor.strokeColor || '',
        textClassName: classNamesForColor.textColor || '',
      });

      return acc;
    },
    []
  );

  if (_.size(stateDescriptions) === 0) {
    return [];
  }

  return stateDescriptions
    .filter(filterNilValues)
    .filter((state: Service.StateDescription) => !!(state.stateName && state.className))
    .filter((state: Service.StateDescription) => state.stateName !== 'Canceled');
};
